<template lang="pug">
.conversation-transcript
  .field-container
    label {{ $t(`${baseTranslationPath}.date_range`) }}
    aiq-date-picker.date-range(
      v-model="dateRange"
      type="daterange"
      :start-placeholder="$t(`${baseTranslationPath}.start_date`)"
      :end-placeholder="$t(`${baseTranslationPath}.end_date`)"
      :placeholder="$t(`${baseTranslationPath}.date_range_placeholder`)"
      placement="bottom-end"
      popper-class="conversation-transcript-date-picker-popup"
      :popper-options="popperOptions"
      :default-time="defaultTime"
      format="YYYY/MM/DD"
      :clearable="true"
      :disabled-date="disableFutureDate"
      :teleported="true"
    )
  .download-button-container
    aiq-button(
      type="primary"
      :disabled="!canDownload || isDownloading"
      @click="download"
    ) {{ $t(`${baseTranslationPath}.download`) }}
</template>

<script>
import dayjs from '@/localPlugins/dayjs';
import { mapState } from 'vuex';
import { downloadTextFile, disableFutureDate, formatMessagesAsTranscriptText } from '@/libs';
import { getDatePickerDefaultTime } from '@/libs/time';
import { getNonReactiveDateRange } from '@/pages/Metrics/configs/dateRange';

export default {
  name: 'ConversationTranscript',
  props: {
    customer: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      dateRange: null,
      isDownloading: false,
      defaultTime: getDatePickerDefaultTime(),
      popperOptions: {
        modifiers: [
          {
            name: 'flip',
            options: {
              fallbackPlacements: ['bottom-start', 'top-end', 'left-start'],
            },
          },
          {
            name: 'preventOverflow',
            options: {
              boundary: 'viewport',
              padding: 8,
            },
          },
        ],
      },
    };
  },
  computed: {
    ...mapState({
      featureFlags: (state) => state.featureFlags,
    }),
    baseTranslationPath() {
      return 'conversation_tab.side_panel.conversation_transcript_accordion';
    },
    canDownload() {
      return Boolean(this.customer?.id) && this.dateRange?.length === 2; // eslint-disable-line no-magic-numbers
    },
  },
  methods: {
    disableFutureDate,

    async download() {
      if (!this.canDownload) return;

      const [startDate, endDate] = getNonReactiveDateRange(this.dateRange);

      this.isDownloading = true;
      try {
        const { messages, customer, agentsById } = await this.$store.dispatch(
          'conversations/downloadConversationTranscript',
          {
            customerId: this.customer.id,
            startDate: dayjs(startDate).startOf('day').toISOString(),
            endDate: dayjs(endDate).endOf('day').toISOString(),
          },
        );
        const fileName = `conversation_transcript_${this.customer.id}_${dayjs(startDate).format('YYYYMMDD')}_${dayjs(endDate).format('YYYYMMDD')}`;
        downloadTextFile(
          formatMessagesAsTranscriptText(messages, {
            startDate,
            endDate,
            customer,
            agentsById,
            translate: (key, options) => this.$t(key, options),
            translationPath: this.baseTranslationPath,
            enableCallTranscript: this.featureFlags.SHOW_CALL_TRANSCRIPT,
          }),
          `${fileName}.txt`,
        );
        this.$aiq.notify.success(this.$t(`${this.baseTranslationPath}.download_success`));
      } catch {
        this.$aiq.notify.error(this.$t(`${this.baseTranslationPath}.download_error`));
      } finally {
        this.isDownloading = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.conversation-transcript {
  .field-container {
    margin-bottom: 16px;

    label {
      display: block;
      margin-bottom: 8px;
      color: var(--aiq-color-text-secondary);
      font-size: 14px;
      font-weight: 400;
    }
  }

  .date-range {
    width: 100%;
  }

  .download-button-container {
    display: flex;
    justify-content: flex-end;
    align-items: center;
  }
}

:deep(.el-date-editor.el-date-editor--daterange) {
  width: 100%;
}
</style>
